/**
 * The eraser's geometry (SRS C4).
 *
 * Everything here is pure: a stroke, a circle, and what is left of the stroke once
 * the circle has passed over it. Kept away from the board and the pointer so the
 * behaviour that matters — a rubbed-out middle leaves the two ends intact — can be
 * proved without a canvas.
 *
 * Area erasing does not edit a stroke. `Stroke` is frozen (docs/05-DATA-MODEL.md §8)
 * and a stroke is never changed after it is drawn, so the original comes off the
 * board and whatever survived goes back on as new strokes carrying the same ink,
 * the same width and the same author.
 */

import { newStrokeId, type Point, type Rect, type Stroke } from '@opal/shared';

import type { InkPoint } from './ink.js';
import { strokePointsOf } from './stroke-bounds.js';

/** How wide the eraser is on screen, in pixels. It feels the same at every zoom. */
export const MIN_ERASER_PX = 8;
export const MAX_ERASER_PX = 120;
export const DEFAULT_ERASER_PX = 24;

/** The square of canvas the eraser can reach from a point. */
export function eraserArea(centre: Point, radius: number): Rect {
  return {
    x: centre.x - radius,
    y: centre.y - radius,
    width: radius * 2,
    height: radius * 2,
  };
}

/** How near ink must come to the centre to be caught: the circle plus the ink's own width. */
function reach(stroke: Stroke, radius: number): number {
  return radius + stroke.canvasWidth / 2;
}

/** Distance from a point to the line segment a–b. */
export function distanceToSegment(p: Point, a: Point, b: Point): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(p.x - a.x, p.y - a.y);
  const along = ((p.x - a.x) * dx + (p.y - a.y) * dy) / lengthSquared;
  const clamped = along < 0 ? 0 : along > 1 ? 1 : along;
  return Math.hypot(p.x - (a.x + clamped * dx), p.y - (a.y + clamped * dy));
}

/**
 * True when any part of the stroke lies under the circle.
 *
 * The segments are tested, not only the recorded points: a line drawn quickly can
 * have half a centimetre between two samples, and an eraser passing through that gap
 * has still gone through the line.
 */
export function strokeTouched(stroke: Stroke, centre: Point, radius: number): boolean {
  const points = strokePointsOf(stroke);
  const limit = reach(stroke, radius);
  const first = points[0];
  if (!first) return false;
  if (points.length === 1) return Math.hypot(centre.x - first.x, centre.y - first.y) <= limit;

  for (let index = 1; index < points.length; index += 1) {
    const from = points[index - 1];
    const to = points[index];
    if (!from || !to) continue;
    if (distanceToSegment(centre, from, to) <= limit) return true;
  }
  return false;
}

/** One surviving run of points, as a stroke in its own right. */
function pieceOf(stroke: Stroke, run: readonly InkPoint[]): Stroke {
  const points = new Float32Array(run.length * 3);
  run.forEach((point, index) => {
    points[index * 3] = point.x;
    points[index * 3 + 1] = point.y;
    points[index * 3 + 2] = point.pressure;
  });
  // A new id, because it is a new stroke on the board; everything else is the
  // original's, because it is still the same person's writing.
  return { ...stroke, id: newStrokeId(), points, createdAt: Date.now() };
}

/** Splits the point list into the runs the circle did not touch. */
function survivingRuns(
  points: readonly InkPoint[],
  centre: Point,
  limit: number,
): { runs: InkPoint[][]; touched: boolean } {
  const runs: InkPoint[][] = [];
  let run: InkPoint[] = [];
  let touched = false;

  const close = (): void => {
    if (run.length > 0) runs.push(run);
    run = [];
  };

  for (let index = 0; index < points.length; index += 1) {
    const point = points[index];
    if (!point) continue;
    if (Math.hypot(centre.x - point.x, centre.y - point.y) <= limit) {
      touched = true;
      close();
      continue;
    }
    run.push(point);
    // Both ends outside but the circle sitting between them: the line still passes
    // under the eraser, so it is cut here as well.
    const next = points[index + 1];
    if (next && distanceToSegment(centre, point, next) <= limit) {
      touched = true;
      close();
    }
  }
  close();
  return { runs, touched };
}

/**
 * What is left of a stroke after the eraser circle has been rubbed over it.
 *
 * Null means the eraser missed it entirely and the board is unchanged. An empty list
 * means the whole stroke went. Anything else is the pieces that survived.
 *
 * A run of a single point is dropped rather than kept: it would leave a dot floating
 * where the writing used to be, which reads as dirt on the board.
 */
export function eraseFromStroke(
  stroke: Stroke,
  centre: Point,
  radius: number,
): Stroke[] | null {
  const points = strokePointsOf(stroke);
  if (points.length === 0) return null;
  const { runs, touched } = survivingRuns(points, centre, reach(stroke, radius));
  if (!touched) return null;
  return runs.filter((run) => run.length > 1).map((run) => pieceOf(stroke, run));
}

/** A ceiling on the fill-in, so one absurd jump can never stall the board. */
const MAX_STEPS = 64;

/**
 * The points to erase at between two positions of the hand.
 *
 * A pointer moving quickly jumps further than the eraser is wide, and erasing only
 * where the samples landed would leave untouched ink in the gaps. Filling the gap in
 * steps no larger than the circle's radius is what makes a fast wipe erase a
 * continuous band rather than a row of holes.
 */
export function stepsBetween(from: Point, to: Point, radius: number): Point[] {
  const gap = Math.hypot(to.x - from.x, to.y - from.y);
  const step = Math.max(radius, 0.5);
  const count = Math.min(Math.ceil(gap / step), MAX_STEPS);
  if (count <= 1) return [to];

  const points: Point[] = [];
  for (let index = 1; index <= count; index += 1) {
    const share = index / count;
    points.push({
      x: from.x + (to.x - from.x) * share,
      y: from.y + (to.y - from.y) * share,
    });
  }
  return points;
}
